import { useState, useEffect, useCallback, useRef } from 'react';
import { TalentCardData } from '../types';
import TalRevAPI, { handleApiError } from '../services/api';

interface TalentCardsListState {
  talentCards: TalentCardData[];
  totalCount: number;
  activeCount: number;
  completedCount: number;
  failedCount: number;
  loading: boolean;
  error: string | null;
}

export const useTalentCardsList = () => {
  const [state, setState] = useState<TalentCardsListState>({
    talentCards: [],
    totalCount: 0,
    activeCount: 0,
    completedCount: 0,
    failedCount: 0,
    loading: false,
    error: null,
  });

  const loadTalentCards = useCallback(async () => {
    setState(prev => ({ ...prev, loading: true, error: null }));

    try {
      const result = await TalRevAPI.listTalentCards();
      
      setState({
        talentCards: result.talentCards,
        totalCount: result.totalCount,
        activeCount: result.activeCount,
        completedCount: result.completedCount,
        failedCount: result.failedCount,
        loading: false,
        error: null,
      });
    } catch (error) {
      const errorMessage = handleApiError(error);
      setState(prev => ({
        ...prev,
        loading: false,
        error: errorMessage,
      }));
    }
  }, []);

  // Load talent cards on mount
  useEffect(() => {
    loadTalentCards();
  }, [loadTalentCards]);

  // Auto-refresh every 15 seconds for real-time updates
  const loadingRef = useRef(false);
  loadingRef.current = state.loading;

  useEffect(() => {
    const interval = setInterval(() => {
      if (!loadingRef.current) {
        loadTalentCards();
      }
    }, 15000);

    return () => clearInterval(interval);
  }, [loadTalentCards]);

  return {
    ...state,
    refresh: loadTalentCards,
  };
};